
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Award,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Coins,
  Copy,
  FileEdit,
  Plus,
  Timer,
  Zap,
} from "lucide-react";
import type {
  MessageResponse,
  CommentResponse,
  EvaluationResponse,
} from "../../gen/models";
import { TurnGovernance } from "./TurnGovernance";
import { HighlightedMessageText } from "./HighlightedMessageText";
import { StayProposalCard } from "./StayProposalCard";
import { ExecutedQueriesSection } from "./ExecutedQueriesSection";
import { useUserDirectory } from "../../hooks/useUserDirectory";
import { useStickyPopover } from "../../hooks/useStickyPopover";
import {
  type AgentInfo,
  type ResolvedRule,
  type ResolvedGuardrail,
  type SourcesGroupResult,
  type ResolvedSourceGroup,
} from "./threadUtils";

interface CopyMessageButtonProps {
  text: string;
  className?: string;
}

function CopyMessageButton({ text, className = "" }: CopyMessageButtonProps) {
  const [copied, setCopied] = useState(false);

  if (!text) return null;

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      className={`absolute z-10 p-1 rounded-md bg-white/80 dark:bg-stone-800/80 hover:bg-white dark:hover:bg-stone-700 text-stone-500 hover:text-stone-800 dark:text-stone-400 dark:hover:text-stone-200 shadow-2xs border border-stone-200/70 dark:border-stone-700/70 transition-all cursor-pointer backdrop-blur-xs ${
        copied ? "!opacity-100" : ""
      } ${className}`}
      title={copied ? "Copied!" : "Copy to clipboard"}
      aria-label={copied ? "Copied!" : "Copy to clipboard"}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </button>
  );
}

interface ThreadMessageTurnProps {
  message: MessageResponse;
  turnIndex: number;
  agentInfo: AgentInfo;
  turnComments: CommentResponse[];
  activeHighlightedCommentId: string | null;
  activeSelection?: {
    text: string;
    start?: number;
    end?: number;
  } | null;
  turnEval?: EvaluationResponse;
  resolvedRules: ResolvedRule[];
  resolvedGuardrails?: ResolvedGuardrail[];
  sourcesGroup: SourcesGroupResult;
  hasThreadComments?: boolean;
  isUserExpanded: boolean;
  isComposerActive?: boolean;
  renderComposerCard?: () => React.ReactNode;
  onToggleUserExpand: (messageId: string) => void;
  onSelectText: (messageId: string) => void;
  onCommentClick: (commentId: string) => void;
  onOpenEvalDrawer: (evalItem: EvaluationResponse, msgId: string, runId: string | null) => void;
  onSimulateTurn: (msgId: string, baselineContent: string, prevUserPrompt?: string) => void;
  onOpenComposerForTurn: (messageId: string, evalId?: string) => void;
  renderCommentCard: (comment: CommentResponse) => React.ReactNode;
  prevUserMessageContent?: string;
  onInspectRule?: (ruleId: string, rule?: ResolvedRule) => void;
  onInspectGuardrail?: (guardrailId: string, guardrail?: ResolvedGuardrail) => void;
  onInspectKnowledge?: (knowledgeId: string, source?: ResolvedSourceGroup) => void;
  onInspectChunk?: (chunkId: string) => void;
  runTelemetry?: Record<string, any>;
  onAddAlteredNote?: (messageId: string, originalMessage: string, actualMessage: string, evalId?: string) => Promise<void> | void;
}

export function ThreadMessageTurn({
  message: msg,
  turnIndex: _turnIndex,
  agentInfo,
  turnComments,
  activeHighlightedCommentId,
  activeSelection,
  turnEval,
  resolvedRules,
  resolvedGuardrails = [],
  sourcesGroup,
  hasThreadComments: _hasThreadComments,
  isUserExpanded,
  isComposerActive = false,
  renderComposerCard,
  onToggleUserExpand,
  onSelectText,
  onCommentClick,
  onOpenEvalDrawer,
  onSimulateTurn: _onSimulateTurn,
  onOpenComposerForTurn,
  renderCommentCard,
  prevUserMessageContent: _prevUserMessageContent,
  onInspectRule,
  onInspectGuardrail,
  onInspectKnowledge,
  onInspectChunk,
  runTelemetry,
  onAddAlteredNote,
}: ThreadMessageTurnProps) {
  const { resolveSenderDisplayName, getUserDisplayName } = useUserDirectory();
  const timePopover = useStickyPopover();
  const isAgent = msg.sender_role === "agent" || msg.sender_role === "assistant";
  const AgentIcon = agentInfo.icon;
  const metadata = (msg.metadata || {}) as Record<string, any>;
  const quoteDraft = metadata.quote_draft as Record<string, any> | undefined;
  const executedQueries = Array.isArray(metadata.executed_queries)
    ? (metadata.executed_queries as string[])
    : undefined;

  const modelName =
    metadata.model ||
    metadata.model_name ||
    metadata.draft?.model ||
    metadata.draft?.model_name ||
    metadata.quote_draft?.model ||
    metadata.quote_draft?.model_name ||
    metadata.telemetry?.model ||
    metadata.telemetry?.model_name ||
    quoteDraft?.model ||
    quoteDraft?.model_name ||
    quoteDraft?.telemetry?.model ||
    quoteDraft?.telemetry?.model_name ||
    runTelemetry?.model ||
    runTelemetry?.model_name;

  const providerName =
    metadata.provider ||
    metadata.draft?.provider ||
    metadata.quote_draft?.provider ||
    metadata.telemetry?.provider ||
    quoteDraft?.provider ||
    quoteDraft?.telemetry?.provider ||
    runTelemetry?.provider ||
    (modelName ? (String(modelName).includes("/") || String(modelName).startsWith("openrouter:") ? "OpenRouter" : String(modelName).toLowerCase().startsWith("gemini-") ? "Google" : undefined) : undefined);

  const tIn =
    metadata.tokens_in ??
    metadata.input_tokens ??
    metadata.telemetry?.tokens_in ??
    metadata.telemetry?.input_tokens ??
    quoteDraft?.telemetry?.tokens_in ??
    runTelemetry?.tokens_in ??
    runTelemetry?.input_tokens;
  const tOut =
    metadata.tokens_out ??
    metadata.output_tokens ??
    metadata.telemetry?.tokens_out ??
    metadata.telemetry?.output_tokens ??
    quoteDraft?.telemetry?.tokens_out ??
    runTelemetry?.tokens_out ??
    runTelemetry?.output_tokens;
  const totalTok =
    metadata.total_tokens ??
    metadata.telemetry?.total_tokens ??
    quoteDraft?.telemetry?.total_tokens ??
    runTelemetry?.total_tokens ??
    (tIn !== undefined && tOut !== undefined ? Number(tIn) + Number(tOut) : undefined);
  const cachedTok =
    metadata.cached_tokens ??
    metadata.prompt_tokens_cached ??
    metadata.telemetry?.cached_tokens ??
    metadata.telemetry?.prompt_tokens_cached ??
    runTelemetry?.cached_tokens ??
    runTelemetry?.prompt_tokens_cached;
  const cost =
    metadata.cost_usd ??
    metadata.estimated_cost_usd ??
    metadata.telemetry?.cost_usd ??
    metadata.telemetry?.estimated_cost_usd ??
    quoteDraft?.telemetry?.estimated_cost_usd ??
    runTelemetry?.cost_usd ??
    runTelemetry?.estimated_cost_usd;

  const durationMs =
    metadata.duration_ms ??
    metadata.latency_ms ??
    (metadata.duration_seconds ? Math.round(Number(metadata.duration_seconds) * 1000) : undefined) ??
    metadata.telemetry?.duration_ms ??
    metadata.telemetry?.latency_ms ??
    (metadata.telemetry?.duration_seconds ? Math.round(Number(metadata.telemetry.duration_seconds) * 1000) : undefined) ??
    quoteDraft?.telemetry?.duration_ms ??
    quoteDraft?.telemetry?.latency_ms ??
    runTelemetry?.duration_ms ??
    runTelemetry?.latency_ms ??
    (runTelemetry?.duration_seconds ? Math.round(Number(runTelemetry.duration_seconds) * 1000) : undefined);

  const ttftMs =
    metadata.ttft_ms ??
    metadata.time_to_first_token_ms ??
    metadata.telemetry?.ttft_ms ??
    metadata.telemetry?.time_to_first_token_ms ??
    runTelemetry?.ttft_ms ??
    runTelemetry?.time_to_first_token_ms;

  const tokensPerSecond =
    durationMs && Number(durationMs) > 0 && tOut && Number(tOut) > 0
      ? (Number(tOut) / (Number(durationMs) / 1000)).toFixed(1)
      : null;

  const originalDraft = (
    metadata.original_draft ||
    metadata.original_content ||
    metadata.original_message ||
    metadata.proposed_draft ||
    ""
  ) as string;

  const wasAltered = Boolean(
    metadata.was_altered ||
    metadata.status === "altered_and_sent" ||
    (originalDraft && originalDraft.trim() !== msg.content?.trim())
  );

  const alteredByUserId = metadata.altered_by_user ? String(metadata.altered_by_user).trim() : null;
  const rawAlteredByName = (metadata.altered_by_name || metadata.altered_by_username || metadata.altered_by) as string | undefined;

  let alteredByDisplayName: string | null = null;
  let isAlteredUserUnresolved = false;

  if (rawAlteredByName && rawAlteredByName.trim() && rawAlteredByName.trim() !== alteredByUserId) {
    alteredByDisplayName = rawAlteredByName.trim();
  } else if (alteredByUserId) {
    const resolved = getUserDisplayName(alteredByUserId);
    if (resolved && resolved !== "Unresolved User" && resolved !== "Patron") {
      alteredByDisplayName = resolved;
    } else {
      alteredByDisplayName = "Unresolved User";
      isAlteredUserUnresolved = true;
    }
  }

  const alteredAtText = metadata.altered_at
    ? new Date(metadata.altered_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : null;

  const hasAlteredNote = turnComments.some(
    (c) => c.comment_text && c.comment_text.includes("Generated completion:")
  );

  const resolvedSenderName = resolveSenderDisplayName({
    senderRole: msg.sender_role,
    senderId: msg.sender_id,
    senderName: msg.sender_name,
    agentType: (metadata.agent_type as string) || agentInfo.name,
  });

  const getAgentCopyText = () => {
    if (quoteDraft) {
      const lines: string[] = [];
      if (quoteDraft.email_subject) lines.push(`Subject: ${quoteDraft.email_subject}`);
      if (quoteDraft.stay_dates_confirmed) lines.push(`Stay Dates: ${quoteDraft.stay_dates_confirmed}`);
      if (quoteDraft.email_body_text) lines.push(quoteDraft.email_body_text);
      else if (quoteDraft.message) lines.push(quoteDraft.message);
      else if (quoteDraft.instagram_dm_text) lines.push(quoteDraft.instagram_dm_text);
      const opts = quoteDraft.quote_options || quoteDraft.options || [];
      if (opts.length > 0) {
        for (const opt of opts) {
          lines.push(
            `${opt.title || opt.residence_name} - ${opt.rate_name || opt.bedrooms_summary || "Direct Web Rate"}: $${Number(opt.total_price_usd || 0).toLocaleString()} ($${Number(opt.nightly_rate_usd || opt.average_nightly_rate_usd || 0).toLocaleString()}/night)`
          );
        }
      }
      return lines.join("\n\n");
    }
    if (msg.content && msg.content.trim()) return msg.content;
    return "";
  };

  const renderMessageContent = () => {
    if (!isAgent) {
      // Human Input on the RIGHT: left aligned text in right aligned box
      return (
        <div className="flex flex-col items-end w-full space-y-1">
          <div className="flex items-center gap-1.5 text-[11px] text-stone-500 pr-2">
            <span className="font-medium text-stone-400">{resolvedSenderName}</span>
            <span>•</span>
            <span>
              {new Date(msg.created_at).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
          <div className="max-w-2xl w-fit ml-auto rounded-3xl px-5 py-3.5 bg-stone-200 dark:bg-[#282a2c] text-stone-900 dark:text-stone-100 text-left text-sm leading-relaxed shadow-xs relative group/bubble">
            {wasAltered && originalDraft && (
              <div className="mb-2 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs text-stone-400 dark:text-stone-500 line-through opacity-70 whitespace-pre-wrap select-text leading-relaxed font-sans">
                    ~{originalDraft}~
                  </div>
                  {onAddAlteredNote && (
                    <button
                      type="button"
                      data-add-altered-note-btn
                      onClick={() => onAddAlteredNote(msg.id, originalDraft, msg.content, turnEval?.id)}
                      className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200 transition cursor-pointer hover:underline shrink-0"
                    >
                      {hasAlteredNote ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                          <span>Added as note</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-3 h-3" />
                          <span>Add as note</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className={!isUserExpanded && msg.content.length > 280 ? "line-clamp-4" : ""}>
              <HighlightedMessageText
                msgId={msg.id}
                content={msg.content}
                comments={turnComments}
                activeHighlightedCommentId={activeHighlightedCommentId}
                activeSelection={activeSelection}
                onSelectText={onSelectText}
                onCommentClick={onCommentClick}
              />
            </div>
            {msg.content.length > 280 && (
              <div className="flex justify-end pt-1 -mb-1">
                <button
                  type="button"
                  onClick={() => onToggleUserExpand(msg.id)}
                  className="inline-flex items-center gap-0.5 text-[11px] text-stone-400 hover:text-stone-200 transition p-0.5 cursor-pointer"
                  title={isUserExpanded ? "Show less" : "Show more"}
                >
                  <span>{isUserExpanded ? "Less" : "More"}</span>
                  {isUserExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                </button>
              </div>
            )}
            <CopyMessageButton
              text={msg.content}
              className="bottom-2 right-2 opacity-0 group-hover/bubble:opacity-100"
            />
          </div>
        </div>
      );
    }

    // Agent / LLM Output on the LEFT: open typography on canvas, no bulky box background
    return (
      <div className="flex flex-col items-start w-full space-y-1.5">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 w-full text-xs">
          <div className="flex items-center gap-1.5 font-semibold text-stone-800 dark:text-stone-200">
            <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${agentInfo.color}`}>
              <AgentIcon className="w-3 h-3" />
            </div>
            <span>{resolvedSenderName}</span>
            <span className="text-stone-400 dark:text-stone-500 font-normal">•</span>

            <div
              ref={timePopover.containerRef}
              className="relative inline-flex items-center font-normal"
              onMouseEnter={timePopover.handleMouseEnter}
              onMouseLeave={timePopover.handleMouseLeave}
            >
              <button
                type="button"
                onClick={timePopover.handleToggleClick}
                className={`inline-flex items-center gap-1 text-[11px] cursor-pointer transition-colors rounded px-1 -mx-1 py-0.5 ${
                  timePopover.isOpen || timePopover.isPinned
                    ? "text-stone-900 dark:text-stone-100 bg-stone-200/60 dark:bg-stone-800 font-medium underline decoration-stone-400 dark:decoration-stone-500 underline-offset-2"
                    : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800/50"
                }`}
                title={
                  timePopover.isPinned
                    ? "Turn details pinned (click to unpin)"
                    : "Click to pin or hover to inspect turn details"
                }
              >
                <span>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </span>
              </button>

              {/* Tooltip / Popover Panel */}
              {timePopover.isOpen && (
                <div
                  className="absolute top-full left-0 mt-1.5 w-72 sm:w-80 rounded-xl border border-stone-200 dark:border-stone-800 bg-white/95 dark:bg-stone-900/95 backdrop-blur-md p-3.5 shadow-xl z-50 text-[11px] font-sans text-stone-800 dark:text-stone-200 space-y-2.5 animate-in fade-in duration-100 text-left"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between pb-1.5 border-b border-stone-100 dark:border-stone-800">
                    <div className="flex items-center gap-1.5 font-medium text-stone-900 dark:text-stone-100">
                      <Clock className="w-3.5 h-3.5 text-stone-500" />
                      <span>Turn Details</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {timePopover.isPinned && (
                        <span className="text-[10px] text-stone-400 dark:text-stone-500 font-mono bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 rounded">
                          Pinned
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-stone-400 dark:text-stone-500">
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                      </span>
                    </div>
                  </div>

                  {/* Model & Trace link */}
                  {(modelName || msg.run_id) && (
                    <div className="space-y-1.5">
                      {modelName && (
                        <div className="flex items-center justify-between">
                          <span className="text-stone-500 dark:text-stone-400">Model</span>
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 border border-stone-200/80 dark:border-stone-700/80 max-w-[180px] truncate"
                            title={`Model: ${modelName}${providerName ? ` (${providerName})` : ""}`}
                          >
                            {modelName}
                          </span>
                        </div>
                      )}

                      {providerName && (
                        <div className="flex items-center justify-between">
                          <span className="text-stone-500 dark:text-stone-400">Provider</span>
                          <span
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-mono text-amber-800 dark:text-amber-300 bg-amber-50/80 dark:bg-amber-950/40 border border-amber-200/60 dark:border-amber-800/60"
                            title={`Provider: ${providerName}`}
                          >
                            {providerName}
                          </span>
                        </div>
                      )}

                      {msg.run_id && (
                        <div className="flex items-center justify-between">
                          <span className="text-stone-500 dark:text-stone-400">Execution Trace</span>
                          <Link
                            to={`/runs/${msg.run_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-600 dark:text-emerald-400 hover:underline"
                            title="Inspect Execution Trace"
                          >
                            <Zap className="w-3 h-3" />
                            <span>{msg.run_id.slice(0, 8)}...</span>
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Altered & sent details */}
                  {wasAltered && (
                    <div className="rounded-lg p-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200/80 dark:border-amber-800/50 space-y-1">
                      <div className="flex items-center gap-1.5 text-amber-800 dark:text-amber-300 font-medium text-[11px]">
                        <FileEdit className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
                        <span>Altered & sent</span>
                      </div>
                      <div className="text-[10px] text-amber-700 dark:text-amber-400/90 pl-5">
                        {alteredByDisplayName ? (
                          isAlteredUserUnresolved ? (
                            <>
                              By <span title={alteredByUserId || undefined} className="underline decoration-dotted cursor-help">Unresolved User</span>
                            </>
                          ) : (
                            `By @${alteredByDisplayName}`
                          )
                        ) : (
                          "Manual modification"
                        )}
                        {alteredAtText && ` at ${alteredAtText}`}
                      </div>
                    </div>
                  )}

                  {/* Evaluation Score */}
                  {turnEval && (
                    <div className="flex items-center justify-between pt-1.5 border-t border-stone-100 dark:border-stone-800">
                      <span className="text-stone-500 dark:text-stone-400">Evaluation</span>
                      <button
                        type="button"
                        onClick={() => onOpenEvalDrawer(turnEval, msg.id, msg.run_id || null)}
                        className={`inline-flex items-center gap-1 text-[11px] font-semibold transition cursor-pointer hover:underline ${
                          turnEval.score >= 4.0
                            ? "text-emerald-600 dark:text-emerald-400"
                            : turnEval.score >= 3.5
                            ? "text-amber-600 dark:text-amber-400"
                            : "text-rose-600 dark:text-rose-400"
                        }`}
                        title="Inspect Evaluation"
                      >
                        <Award className="w-3.5 h-3.5" />
                        <span>{Number(turnEval.score).toFixed(1)} / 5.0</span>
                        {turnEval.status === "flagged" && (
                          <span className="inline-flex items-center gap-0.5 text-rose-500 text-[10px] ml-1">
                            <AlertTriangle className="w-3 h-3" /> Flagged
                          </span>
                        )}
                      </button>
                    </div>
                  )}

                  {/* Latency Details */}
                  {(durationMs !== undefined || ttftMs !== undefined) && (
                    <div className="space-y-1.5 pt-1.5 border-t border-stone-100 dark:border-stone-800">
                      <div className="flex items-center justify-between text-stone-500 dark:text-stone-400">
                        <span className="flex items-center gap-1.5">
                          <Timer className="w-3.5 h-3.5 text-stone-500" />
                          <span>Latency</span>
                        </span>
                        <span className="font-mono font-medium text-stone-800 dark:text-stone-200">
                          {durationMs !== undefined && Number(durationMs) > 0
                            ? Number(durationMs) >= 1000
                              ? `${(Number(durationMs) / 1000).toFixed(2)}s`
                              : `${Math.round(Number(durationMs))}ms`
                            : "—"}
                        </span>
                      </div>

                      {tokensPerSecond !== null && (
                        <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 pl-5 font-mono">
                          <span>Generation Speed</span>
                          <span className="text-stone-700 dark:text-stone-300 font-medium">
                            {tokensPerSecond} tok/s
                          </span>
                        </div>
                      )}

                      {ttftMs !== undefined && Number(ttftMs) > 0 && (
                        <div className="flex items-center justify-between text-[10px] text-stone-500 dark:text-stone-400 pl-5 font-mono">
                          <span>Time to First Token</span>
                          <span className="text-stone-700 dark:text-stone-300 font-medium">
                            {Math.round(Number(ttftMs))}ms
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Turn Token & Cost Breakdown */}
                  {(totalTok !== undefined || cost !== undefined) && (
                    <div className="space-y-1.5 pt-1.5 border-t border-stone-100 dark:border-stone-800 font-mono text-[10px]">
                      <div className="flex items-center justify-between font-sans text-[11px] font-medium text-stone-900 dark:text-stone-100 pb-0.5">
                        <span className="flex items-center gap-1.5">
                          <Coins className="w-3.5 h-3.5 text-amber-500" />
                          <span>Usage & Cost</span>
                        </span>
                        {cost !== undefined && Number(cost) > 0 && (
                          <span className="font-mono text-emerald-600 dark:text-emerald-400 font-semibold">
                            ${Number(cost) < 0.01 ? Number(cost).toFixed(4) : Number(cost).toFixed(3)}
                          </span>
                        )}
                      </div>
                      {totalTok !== undefined && (
                        <div className="flex items-center justify-between">
                          <span className="text-stone-500 dark:text-stone-400">Total Tokens</span>
                          <span className="font-semibold text-stone-900 dark:text-stone-100">
                            {Number(totalTok).toLocaleString()}
                          </span>
                        </div>
                      )}

                      {(tIn !== undefined || tOut !== undefined) && (
                        <div className="flex items-center justify-between text-stone-500 dark:text-stone-400 pl-2">
                          <span>Prompt / Completion</span>
                          <span>
                            {tIn !== undefined ? Number(tIn).toLocaleString() : "-"} in / {tOut !== undefined ? Number(tOut).toLocaleString() : "-"} out
                          </span>
                        </div>
                      )}

                      {cachedTok !== undefined && Number(cachedTok) > 0 && (
                        <div className="flex items-center justify-between text-indigo-600 dark:text-indigo-400 pl-2">
                          <span>Prompt Cache</span>
                          <span>⚡ {Number(cachedTok).toLocaleString()}</span>
                        </div>
                      )}

                      {cost !== undefined && (
                        <div className="flex items-center justify-between pt-1 border-t border-stone-100 dark:border-stone-800">
                          <span className="text-stone-500 dark:text-stone-400">Estimated Cost</span>
                          <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                            ${Number(cost) < 0.01 ? Number(cost).toFixed(4) : Number(cost).toFixed(3)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Fallback if completely empty */}
                  {!modelName && !msg.run_id && !wasAltered && !turnEval && durationMs === undefined && totalTok === undefined && cost === undefined && (
                    <div className="text-stone-400 dark:text-stone-500 text-center py-2 text-[10px]">
                      No telemetry recorded for this turn
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Add note (restricted to LLM/agent entries, placed directly on the card) */}
          <button
            type="button"
            data-add-note-btn
            onClick={() => onOpenComposerForTurn(msg.id, turnEval?.id)}
            className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-700 dark:text-stone-500 dark:hover:text-stone-300 transition cursor-pointer hover:underline ml-auto shrink-0"
            title="Add note to this turn"
          >
            <Plus className="w-3 h-3" />
            <span>Add note</span>
          </button>
        </div>

        {/* LLM Output Body */}
        <div className="w-full text-left text-sm leading-relaxed text-stone-900 dark:text-[#e3e3e3] pl-6 md:pl-7 space-y-3">
          <div className="relative group/agent-body space-y-3">
            {/* Altered message original version */}
            {wasAltered && originalDraft && (
              <div className="space-y-1 mb-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-xs text-stone-400 dark:text-stone-500 line-through opacity-70 whitespace-pre-wrap select-text leading-relaxed font-sans">
                    ~{originalDraft}~
                  </div>
                  {onAddAlteredNote && (
                    <button
                      type="button"
                      data-add-altered-note-btn
                      onClick={() => {
                        const actualText =
                          msg.content && msg.content !== "[Luxury Quote Card Posted]"
                            ? msg.content
                            : getAgentCopyText();
                        onAddAlteredNote(msg.id, originalDraft, actualText, turnEval?.id);
                      }}
                      className="inline-flex items-center gap-1 text-[11px] text-stone-400 hover:text-stone-700 dark:text-stone-500 dark:hover:text-stone-300 transition cursor-pointer hover:underline shrink-0"
                      title="Add as note comparing generated vs actual completion to batch"
                    >
                      {hasAlteredNote ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                          <span>Added as note</span>
                        </>
                      ) : (
                        <>
                          <Plus className="w-3 h-3" />
                          <span>Add as note</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {msg.content && msg.content !== "[Luxury Quote Card Posted]" && quoteDraft && (
              <HighlightedMessageText
                msgId={msg.id}
                content={msg.content}
                comments={turnComments}
                activeHighlightedCommentId={activeHighlightedCommentId}
                activeSelection={activeSelection}
                onSelectText={onSelectText}
                onCommentClick={onCommentClick}
              />
            )}
            {quoteDraft ? (
              <StayProposalCard
                quoteDraft={quoteDraft}
                msgId={msg.id}
                comments={turnComments}
                activeHighlightedCommentId={activeHighlightedCommentId}
                activeSelection={activeSelection}
                onSelectText={onSelectText}
                onCommentClick={onCommentClick}
              />
            ) : (
              <HighlightedMessageText
                msgId={msg.id}
                content={msg.content}
                comments={turnComments}
                activeHighlightedCommentId={activeHighlightedCommentId}
                activeSelection={activeSelection}
                onSelectText={onSelectText}
                onCommentClick={onCommentClick}
              />
            )}
            <CopyMessageButton
              text={getAgentCopyText()}
              className="bottom-1 right-1 opacity-0 group-hover/agent-body:opacity-100"
            />
          </div>

          {/* Executed SQL Queries Section */}
          {executedQueries && executedQueries.length > 0 && (
            <ExecutedQueriesSection queries={executedQueries} />
          )}

          {/* Governance Rules & Sources */}
          <TurnGovernance
            rules={resolvedRules}
            guardrails={resolvedGuardrails}
            sourcesGroup={sourcesGroup}
            onInspectRule={onInspectRule}
            onInspectGuardrail={onInspectGuardrail}
            onInspectKnowledge={onInspectKnowledge}
            onInspectChunk={onInspectChunk}
          />
        </div>
      </div>
    );
  };

  return (
    <div
      id={`msg-${msg.id}`}
      className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start group/turn"
    >
      <div className="lg:col-span-8 space-y-1.5">
        {renderMessageContent()}
      </div>
      <div className="lg:col-span-4 space-y-2.5 pt-6">
        {turnComments.map((c) => renderCommentCard(c))}
        {isComposerActive && renderComposerCard ? renderComposerCard() : null}
      </div>
    </div>
  );
}
